/**
 * How often we re-ask live systems (Fleetio especially) after a pull.
 *
 * Load reuses the last SHARED collect (kpi_collection_runs + kpi_entries) so
 * Komil and Zafar see the same numbers and do not hit Fleetio again. Only
 * Refresh from systems (or cron) asks the APIs. A failed pull can retry after
 * two minutes.
 */

export const KPI_COLLECT_SUCCESS_TTL_MS = 6 * 60 * 60 * 1000;
export const KPI_COLLECT_RETRY_TTL_MS = 2 * 60 * 1000;

export interface KpiCollectMeta {
  at: string;
  status: "success" | "partial" | "failed";
  fleetioOk: boolean;
  periodTypes?: Array<"week" | "month">;
  /** True when the timestamp came from kpi_collection_runs, not this browser. */
  shared?: boolean;
}

export function collectCooldownMs(meta: KpiCollectMeta): number {
  return meta.status === "failed" || !meta.fleetioOk
    ? KPI_COLLECT_RETRY_TTL_MS
    : KPI_COLLECT_SUCCESS_TTL_MS;
}

/** Load may reuse this pull instead of calling Fleetio/Samsara/QuickManage. */
export function canReuseCollect(
  meta: KpiCollectMeta | null,
  now = Date.now(),
  periodType?: "week" | "month",
): boolean {
  if (!meta) return false;
  const at = Date.parse(meta.at);
  if (!Number.isFinite(at)) return false;
  if (periodType && meta.periodTypes?.length && !meta.periodTypes.includes(periodType)) {
    return false;
  }
  if (meta.status === "failed" || !meta.fleetioOk) {
    return now - at < KPI_COLLECT_RETRY_TTL_MS;
  }
  return true;
}

export function isCollectFresh(
  meta: KpiCollectMeta | null,
  now = Date.now(),
  periodType?: "week" | "month",
): boolean {
  return canReuseCollect(meta, now, periodType);
}

export function nextCollectAllowedAt(meta: KpiCollectMeta): Date {
  if (meta.status !== "failed" && meta.fleetioOk) {
    return new Date(Date.parse(meta.at));
  }
  return new Date(Date.parse(meta.at) + KPI_COLLECT_RETRY_TTL_MS);
}

export function humanizeCollectorLine(
  collector: string,
  note?: string,
  error?: string,
): string | null {
  const text = `${note ?? ""} ${error ?? ""}`.trim();
  if (!text) return null;
  const lower = text.toLowerCase();
  if (collector === "cache") return text;
  if (lower.includes("429") || lower.includes("too many requests")) {
    return "Fleetio asked us to slow down. Wait a minute, then click Refresh from systems.";
  }
  if (lower.includes("enddate") || lower.includes("on or before the current date")) {
    return "Samsara will not return future dates. Refresh again — the window now stops at today.";
  }
  if (lower.includes("failed to search trips") || (collector === "quickmanage" && lower.includes("500"))) {
    return "QuickManage trip search failed on date filters. Refresh from systems to search without them.";
  }
  if (lower.includes("webkey")) {
    return "Safety CSA / HazMat skipped until management answers Q16 (free FMCSA webkey).";
  }
  if (lower.includes("load_status_events") || lower.includes("not migrated")) {
    return "On-time and billing-cycle wait for Phase E (status-history table).";
  }
  return `${collector}: ${text.slice(0, 180)}`;
}
